import { useState } from "react";
import useLatest from "@-/com.npmjs.use-latest";
import type { AppSelectionItem, AppSelectionScope } from "#app-selection-item";

// The app-layout context now holds ONLY the morpheme multi-select state. The
// actor `user` row (with its per-user settings) is read synchronously via
// `useActorUser()` from `@-/dev.improve.haniverse.lib.user`, and the Favorites
// collection via the favorites-read package's sync hooks — neither is threaded
// through this context anymore.
export function useAppLayoutContextValue() {
  // App-wide multi-select state for morpheme surfaces and concrete user-card buttons.
  // The ordered map preserves enough metadata for the bottom overlay to dispatch an action to
  // either a conceptual morpheme or an exact user_card row. It lives above the app navigator so
  // selection survives view changes and the action overlay can be mounted once for the app shell.
  //
  // Only raw state + setters live in the context value; the derived operations (enter selecting,
  // toggle, clear) are composed in `@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.tile.selection`.
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionScope, setSelectionScope] = useState<AppSelectionScope | null>(null);
  const [selectedItems, setSelectedItems] = useState<ReadonlyMap<string, AppSelectionItem>>(
    () => new Map(),
  );
  // One stable handle for the operations. Every selectable tile composes them, and a grid mounts
  // hundreds of tiles: subscribing each tile to the scope and three setters separately cost four
  // context listeners per tile. The scope is only needed when a press is handled, so it is read then.
  const selectionScopeRef = useLatest(selectionScope);
  const [selectionActions] = useState(() => ({
    getSelectionScope: () => selectionScopeRef.current,
    setIsSelecting,
    setSelectedItems,
    setSelectionScope,
  }));

  return {
    isSelecting,
    selectedItems,
    selectionActions,
    selectionScope,
    setIsSelecting,
    setSelectedItems,
    setSelectionScope,
  };
}

export type AppLayoutContextValue = ReturnType<typeof useAppLayoutContextValue>;
